import { useState, useRef, useEffect } from 'react';
import { useSearchContext } from '../../../contexts/SearchContext';
import { syncPreferencesToCookies } from '../../../utils/shared/client-preferences';

export type ViewMode = 'grid' | 'list' | 'map';

export interface UseViewModeProps {
  initialLayout?: string | null;
}

export interface UseViewModeReturn {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
}

/**
 * Hook to manage view mode state (grid/list/map)
 * Syncs with SearchContext and localStorage
 */
export function useViewMode({ initialLayout }: UseViewModeProps = {}): UseViewModeReturn {
  const { viewMode: contextViewMode, setViewMode: setContextViewMode } = useSearchContext();

  // Initialize viewMode from SSR or localStorage, sync to context
  const [viewMode, setViewModeState] = useState<ViewMode>(() => {
    // Prefer SSR-provided layout first to avoid hydration switch
    if (initialLayout === 'grid' || initialLayout === 'list' || initialLayout === 'map') {
      return initialLayout;
    }
    // Fallback: read cookie-backed localStorage on client after hydration
    if (typeof window !== 'undefined') {
      const stored = window.localStorage.getItem('jobcrawls.viewMode') || window.localStorage.getItem('layout');
      if (stored === 'list' || stored === 'grid' || stored === 'map') {
        return stored as ViewMode;
      }
    }
    return 'list';
  });

  // Sync local viewMode to context on mount and when it changes
  // Use useRef to prevent infinite loops - only sync when viewMode actually changes
  const prevViewModeRef = useRef(viewMode);
  const hasSyncedRef = useRef(false);
  useEffect(() => {
    // Only sync if viewMode changed (not if contextViewMode changed)
    // Also only sync once on mount if they're different, to avoid unnecessary updates
    if (prevViewModeRef.current !== viewMode && contextViewMode !== viewMode) {
      // On first mount, only sync if they're significantly different (not just initialization)
      if (!hasSyncedRef.current || prevViewModeRef.current !== viewMode) {
        setContextViewMode(viewMode);
        prevViewModeRef.current = viewMode;
        hasSyncedRef.current = true;
      }
    }
  }, [viewMode, contextViewMode, setContextViewMode]);

  // Wrapper function to update viewMode and sync to cookies and context
  const setViewMode = (newViewMode: ViewMode) => {
    setViewModeState(newViewMode);
    setContextViewMode(newViewMode);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('jobcrawls.viewMode', newViewMode);
      window.localStorage.setItem('layout', newViewMode); // Also store in new format
      syncPreferencesToCookies();
    }
  };

  return {
    viewMode,
    setViewMode,
  };
}
